import React, { useEffect, useState } from "react";
import { Download, ExternalLink, Sheet } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { fmtWhen } from "../labels";

const btn = "inline-flex items-center gap-1.5 text-xs border border-clay-300 bg-white px-3 py-1.5 hover:border-clay-900 disabled:opacity-50";

/* On-demand exports: PDF download and a rewrite of the agency's «Informe KimKim» Google Sheet. */
export function ExportBar({ queryParams }) {
  const [pdfBusy, setPdfBusy] = useState(false);
  const [sheetBusy, setSheetBusy] = useState(false);
  const [sheet, setSheet] = useState(null);
  useEffect(() => { api.get("/kimkim/report/sheets").then(({ data }) => setSheet(data)).catch(() => {}); }, []);

  const downloadPdf = async () => {
    setPdfBusy(true);
    try {
      const { data, headers } = await api.get("/kimkim/report/pdf", { params: queryParams, responseType: "blob" });
      const name = /filename="([^"]+)"/.exec(headers["content-disposition"] || "")?.[1] || "informe-kimkim.pdf";
      const url = URL.createObjectURL(data);
      const a = Object.assign(document.createElement("a"), { href: url, download: name });
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
    } catch (e) {
      let msg = "No se pudo generar el PDF";
      try { msg = JSON.parse(await e?.response?.data?.text())?.detail || msg; } catch { /* keep default */ }
      toast.error(String(msg));
    } finally { setPdfBusy(false); }
  };

  const toSheets = async () => {
    setSheetBusy(true);
    try {
      const { data } = await api.post("/kimkim/report/sheets", null, { params: queryParams });
      setSheet((s) => ({ ...s, connected: true, url: data.url, last_export_at: new Date().toISOString() }));
      toast.success(`Hoja actualizada: ${data.rows} filas en ${data.tabs.length} pestañas`, { action: { label: "Abrir", onClick: () => window.open(data.url, "_blank") } });
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "No se pudo escribir en Google Sheets");
    } finally { setSheetBusy(false); }
  };

  return (
    <div className="flex flex-wrap items-center gap-2" data-testid="kk-export-bar">
      <button type="button" onClick={downloadPdf} disabled={pdfBusy} className={btn} data-testid="kk-export-pdf">
        <Download size={12} className={pdfBusy ? "animate-bounce" : ""}/> {pdfBusy ? "Generando PDF…" : "Descargar PDF"}
      </button>
      {sheet?.connected ? (
        <>
          <button type="button" onClick={toSheets} disabled={sheetBusy} className={btn} data-testid="kk-export-sheets">
            <Sheet size={12}/> {sheetBusy ? "Escribiendo…" : "Enviar a Google Sheets"}
          </button>
          {sheet.url && (
            <a href={sheet.url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs text-clay-600 hover:text-terracotta" data-testid="kk-export-sheets-link">
              <ExternalLink size={11}/> Abrir hoja{sheet.last_export_at ? <span className="text-clay-400"> · {fmtWhen(sheet.last_export_at)}</span> : null}
            </a>
          )}
        </>
      ) : sheet && (
        <a href="/admin/marketing" className="inline-flex items-center gap-1.5 text-xs text-clay-600 hover:text-terracotta" data-testid="kk-export-sheets-connect" title="Google Sheets usa la conexión de Google de Marketing & API">
          <Sheet size={12}/> Google Sheets: conecta Google en Marketing &amp; API
        </a>
      )}
    </div>
  );
}
